import { PublicClientApplication, Configuration, LogLevel } from "@azure/msal-browser";

/**
 * MSAL (Microsoft Authentication Library) configuration for MS Entra ID.
 *
 * - Defines `b2cPolicies` for sign-in, sign-up, and profile edit flows using env vars.
 * - Configures MSAL with:
 *    - Client ID and authority from the tenant.
 *    - `knownAuthorities` to whitelist the CIAM domain.
 *    - Redirect and logout URIs from env vars.
 *    - Local storage caching for auth state.
 * - Creates a singleton `msalInstance` for use throughout the app.
 * - Exports `apiScopes` array from `REACT_APP_MS_ENTRA_ID_API_SCOPE` (empty if not set).
 */

const host         = process.env.REACT_APP_IDENTITY_HOST!;                 // e.g. nrtbdev.ciamlogin.com
const tenantId     = process.env.REACT_APP_MS_ENTRA_ID_TENANT_ID!;         // GUID
const clientId     = process.env.REACT_APP_MS_ENTRA_ID_CLIENT_ID!;
const redirectUri  = typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_MS_ENTRA_ID_REDIRECT_URI!;
const logoutUri    = typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_MS_ENTRA_ID_LOGOUT_URI!;
const signInPolicy = process.env.REACT_APP_MS_ENTRA_ID_SIGNIN_POLICY!;     // e.g. "sign-up"
const signUpPolicy = process.env.REACT_APP_MS_ENTRA_ID_SIGNUP_POLICY!;     // e.g. "sign-up"
const editPolicy   = process.env.REACT_APP_MS_ENTRA_ID_EDIT_PROFILE_POLICY || "";

// v2.0 tenant authority (policy is appended as ?p=)
const baseAuthority = `https://${host}/${tenantId}/v2.0`;
export const b2cPolicies = {
    names: { signIn: signInPolicy, signUp: signUpPolicy, editPolicy },
    authorities: {
        signIn:      { authority: baseAuthority },
        signUp:      { authority: baseAuthority },
        editProfile: { authority: baseAuthority },
    },
    authorityDomain: host,
};

const msalConfig: Configuration = {
    auth: {
        clientId,
        authority: b2cPolicies.authorities.signIn.authority,  // v2.0 + ?p=<policy>
        knownAuthorities: [b2cPolicies.authorityDomain],       // required for custom CIAM domain
        redirectUri,
        postLogoutRedirectUri: logoutUri,
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (_level, _message, _containsPii) => {},
            logLevel: LogLevel.Warning,
        },
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const oidcScopes = ["openid", "profile", "email"];

// Scopes array for our API (comma-separated env var, can be blank)
export const apiScopes: string[] = (process.env.REACT_APP_MS_ENTRA_ID_API_SCOPE || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);
